import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

// Local date (yyyy-MM-dd) without timezone shift
const getTodayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export function usePickupRequests() {
  const [pickupRequests, setPickupRequests] = useState([]);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Default to "today" so the first load / courier pick doesn't pull the full history
  const [filters, setFilters] = useState({ courier: "", date_from: getTodayStr(), date_to: "" });

  const fetchPickupRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.courier) params.courier = filters.courier;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await axios.get("pickup-requests/", { params });
      setPickupRequests(data);
    } catch (e) {
      setError("Error al cargar las solicitudes de retiro.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchEligibleOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.courier) params.courier = filters.courier;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await axios.get("pickup-requests/eligible-orders/", { params });
      setEligibleOrders(data);
    } catch (e) {
      setError("Error al cargar los pedidos elegibles.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchAvailableCouriers = useCallback(async () => {
    try {
      const { data } = await axios.get("pickup-requests/couriers/");
      setAvailableCouriers(data);
    } catch (e) {
      console.error("Error al cargar couriers disponibles:", e);
    }
  }, []);

  const createPickupRequest = useCallback(async (courier) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post("pickup-requests/", { courier });
      setPickupRequests((prev) => [data, ...prev]);
      setSuccess(`Solicitud ${data.external_pickup_id} creada correctamente.`);
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al crear la solicitud de retiro.";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const assignOrders = useCallback(async (pickupId, orderIds) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `pickup-requests/${pickupId}/assign-orders/`,
        { order_ids: orderIds }
      );
      await fetchPickupRequests();
      setSuccess("Pedidos asignados correctamente.");
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al asignar los pedidos.";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [fetchPickupRequests]);

  const requestManifest = useCallback(async (pickupId) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`pickup-requests/${pickupId}/manifest/`);
      await fetchPickupRequests();
      setSuccess("Manifiesto generado correctamente.");
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al obtener el manifiesto.";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [fetchPickupRequests]);

  useEffect(() => {
    fetchAvailableCouriers();
  }, [fetchAvailableCouriers]);

  useEffect(() => {
    fetchPickupRequests();
  }, [fetchPickupRequests]);

  return {
    pickupRequests,
    eligibleOrders,
    availableCouriers,
    loading,
    error,
    setError,
    success,
    setSuccess,
    filters,
    setFilters,
    fetchPickupRequests,
    fetchEligibleOrders,
    createPickupRequest,
    assignOrders,
    requestManifest,
  };
}
